import { PublicRoutes } from "@/constants/routes";
import { FileCheck2, Handshake, Siren, UserRoundCheck } from "lucide-react";
import { ACCOUNT_TERMS, DPO } from "./company";

const { readOnlyDays, retentionDays } = ACCOUNT_TERMS;
import type { LegalDocument } from "./types";

export const LGPD: LegalDocument = {
  key: "lgpd",
  path: PublicRoutes.LGPD,
  eyebrow: "Proteção de dados",
  title: "LGPD na Fidexa",
  seoTitle: "LGPD na Fidexa: controlador, operador e direitos",
  description:
    "Como a Fidexa cumpre a LGPD: o papel da loja e o da Fidexa, o que cabe a cada um, fornecedores, incidentes e como pedir seus direitos ao encarregado de dados.",
  intro:
    "A LGPD diz quem responde pelo quê quando dados pessoais são usados. Esta página mostra como isso funciona na Fidexa: o que é responsabilidade da loja, o que é nossa e como você exerce seus direitos.",
  version: "1.0",
  updatedAt: "2026-10-12",

  highlights: [
    {
      icon: Handshake,
      title: "Papéis claros",
      text: "A loja é a controladora dos dados dos clientes dela. A Fidexa é a operadora e só age conforme as instruções da loja.",
    },
    {
      icon: FileCheck2,
      title: "Sem uso próprio",
      text: "Não usamos os dados dos clientes das lojas para finalidades nossas, nem os vendemos ou compartilhamos para marketing.",
    },
    {
      icon: Siren,
      title: "Incidente, aviso rápido",
      text: "Se houver incidente com dados de uma loja, avisamos a loja em até 48 horas após a confirmação.",
    },
    {
      icon: UserRoundCheck,
      title: "Encarregado nomeado",
      text: `${DPO.name} é o encarregado de dados da Fidexa e responde pelo e-mail ${DPO.email}.`,
    },
  ],

  sections: [
    {
      id: "papeis",
      title: "Quem é controlador e quem é operador",
      summary:
        "Nos dados dos clientes do crediário, a loja decide e a Fidexa executa. Nos dados de lojistas, usuários e visitantes do site, quem decide é a Fidexa.",
      blocks: [
        {
          type: "paragraph",
          text: "Pela LGPD (art. 5º, VI e VII), **controlador** é quem toma as decisões sobre o tratamento dos dados e **operador** é quem trata os dados em nome do controlador. A Fidexa ocupa os dois papéis, em situações diferentes:",
        },
        {
          type: "list",
          items: [
            "**Operadora** dos dados dos clientes do crediário que a loja cadastra ou importa na plataforma. A loja define para que os dados são usados e com que base legal.",
            "**Controladora** dos dados de lojistas, usuários da plataforma, interessados que pedem demonstração e visitantes do site.",
          ],
        },
        {
          type: "paragraph",
          text: `Os detalhes de cada caso estão na [Política de Privacidade](${PublicRoutes.POLITICA_PRIVACIDADE}#dois-papeis).`,
        },
      ],
    },
    {
      id: "responsabilidade-da-loja",
      title: "O que cabe à loja",
      summary:
        "Como controladora, a loja responde pela base legal para tratar os dados dos clientes, pela qualidade desses dados e pela forma como faz a cobrança.",
      blocks: [
        {
          type: "list",
          items: [
            "Ter base legal para tratar os dados dos clientes e para comunicar-se com eles sobre o crediário, como a execução do contrato de compra parcelada (LGPD, art. 7º, V).",
            "Garantir que os dados inseridos na plataforma são verdadeiros, atualizados e obtidos de forma lícita.",
            "Definir o conteúdo, o tom e a frequência das mensagens, respeitando o Código de Defesa do Consumidor: sem expor o cliente a ridículo, constrangimento ou ameaça (arts. 42 e 71).",
            "Respeitar o pedido do cliente que não quer mais receber mensagens.",
            "Responder aos pedidos dos clientes sobre os dados deles e, se houver incidente, comunicar a ANPD e os titulares quando a lei exigir.",
            "Manter as credenciais de acesso em sigilo e dar acesso à plataforma só a quem precisa.",
          ],
        },
      ],
    },
    {
      id: "compromissos-da-fidexa",
      title: "O que a Fidexa faz como operadora",
      summary:
        "Seguimos só as instruções da loja, protegemos os dados, ajudamos a loja a cumprir a LGPD e, no fim do contrato, entregamos a cópia e eliminamos os dados nos prazos combinados.",
      blocks: [
        {
          type: "list",
          items: [
            "Tratar os dados **só conforme as instruções lícitas da loja** e para prestar o serviço contratado (LGPD, art. 39). Não usamos esses dados para finalidades próprias.",
            "Manter sigilo sobre os dados e exigir o mesmo de quem trabalha com eles.",
            "Adotar medidas de segurança técnicas e administrativas (art. 46). Veja a página [Segurança](/seguranca).",
            "Manter o registro das operações de tratamento que realizamos (art. 37).",
            "Dar à loja as informações de que ela precisar para elaborar o relatório de impacto à proteção de dados (art. 38) e para responder à ANPD.",
            "Encaminhar à loja, em até 5 dias úteis, os pedidos de titulares que chegarem até nós.",
            `Ao fim do contrato, deixar a plataforma disponível para leitura e enviar a cópia dos dados, se a loja pedir, por ${readOnlyDays} dias; guardar os dados bloqueados até completar ${retentionDays} dias; e então eliminá-los, exceto o que a lei mandar guardar. Os detalhes estão na [Política de Cancelamento](${PublicRoutes.POLITICA_CANCELAMENTO}#depois-do-cancelamento).`,
          ],
        },
        {
          type: "paragraph",
          text: "Estatísticas agregadas e anonimizadas, que não identificam ninguém, não são dados pessoais (art. 12) e podem ser usadas pela Fidexa, por exemplo para divulgar o volume total recuperado pela plataforma.",
        },
      ],
    },
    {
      id: "bases-legais",
      title: "Bases legais quando a Fidexa é controladora",
      summary:
        "Usamos principalmente a execução do contrato. Consentimento só para cookies de análise e de anúncios.",
      blocks: [
        {
          type: "table",
          caption: "Bases legais da Fidexa como controladora (LGPD, art. 7º)",
          columns: [
            { key: "base", header: "Base legal", primary: true },
            { key: "quando", header: "Quando usamos" },
          ],
          rows: [
            {
              base: "Execução de contrato (art. 7º, V)",
              quando:
                "Criar e manter a conta, prestar o serviço, faturar e dar suporte.",
            },
            {
              base: "Obrigação legal (art. 7º, II)",
              quando:
                "Guardar registros de acesso (Marco Civil, art. 15) e documentos fiscais.",
            },
            {
              base: "Legítimo interesse (art. 7º, IX)",
              quando:
                "Responder pedidos de demonstração, enviar conteúdo sobre a Fidexa e prevenir fraude, sempre com opção de oposição.",
            },
            {
              base: "Exercício regular de direitos (art. 7º, VI)",
              quando:
                "Defender a Fidexa em processos judiciais, administrativos ou arbitrais.",
            },
            {
              base: "Consentimento (art. 7º, I)",
              quando: "Cookies de análise e de anúncios no site.",
            },
          ],
        },
      ],
    },
    {
      id: "suboperadores",
      title: "Fornecedores que tratam dados (suboperadores)",
      summary:
        "Usamos poucos fornecedores, cada um com uma função. Quando a lista mudar, avisamos as lojas antes.",
      blocks: [
        {
          type: "paragraph",
          text: "Para prestar o serviço, contamos com fornecedores que tratam dados em nosso nome. Eles estão sujeitos a contrato e a dever de sigilo. Ao contratar a Fidexa, a loja autoriza o uso destes fornecedores:",
        },
        {
          type: "table",
          caption: "Suboperadores da plataforma",
          columns: [
            { key: "quem", header: "Fornecedor", primary: true },
            { key: "funcao", header: "Função" },
          ],
          rows: [
            {
              quem: "Meta Platforms (WhatsApp Business Platform)",
              funcao: "Entrega das mensagens da loja pelo WhatsApp.",
            },
            {
              quem: "Hostinger",
              funcao: "Servidores da aplicação e banco de dados.",
            },
            {
              quem: "Microsoft Azure",
              funcao: "Armazenamento das cópias de segurança.",
            },
            {
              quem: "Resend",
              funcao: "Envio de e-mails do serviço.",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Se incluirmos ou trocarmos um suboperador, atualizamos esta lista e avisamos as lojas com antecedência. A loja que discordar pode falar com o encarregado.",
        },
      ],
    },
    {
      id: "transferencia-internacional",
      title: "Transferência internacional",
      summary:
        "Alguns fornecedores processam dados fora do Brasil. Essas transferências seguem o art. 33 da LGPD e a Resolução CD/ANPD nº 19/2024.",
      blocks: [
        {
          type: "paragraph",
          text: "Meta, Microsoft, Google e outros fornecedores podem processar dados em servidores nos Estados Unidos ou na União Europeia. Nesses casos, a transferência se apoia nos mecanismos do art. 33 da LGPD e da Resolução CD/ANPD nº 19/2024, como cláusulas contratuais de proteção de dados.",
        },
        {
          type: "paragraph",
          text: `Você pode pedir ao encarregado informações sobre os países de destino e os mecanismos usados em cada transferência; respondemos em até 15 dias.`,
        },
      ],
    },
    {
      id: "direitos-do-titular",
      title: "Direitos do titular",
      summary:
        "Pedidos sobre seus dados chegam ao encarregado. Se o dado for de uma loja, encaminhamos a ela em até 5 dias úteis e avisamos você.",
      blocks: [
        {
          type: "paragraph",
          text: `A LGPD (art. 18) garante direitos como confirmação, acesso, correção, eliminação, portabilidade e revogação do consentimento. A lista completa está na [Política de Privacidade](${PublicRoutes.POLITICA_PRIVACIDADE}#seus-direitos).`,
        },
        {
          type: "list",
          ordered: true,
          items: [
            `Escreva para [${DPO.email}](mailto:${DPO.email}) dizendo o que você quer.`,
            "Podemos pedir uma confirmação de identidade, para não entregar seus dados a outra pessoa.",
            "Se os dados forem da Fidexa como controladora, respondemos em até 15 dias.",
            "Se os dados forem de uma loja, encaminhamos o pedido a ela em até 5 dias úteis, avisamos você e apoiamos a loja na resposta.",
          ],
        },
        {
          type: "paragraph",
          text: "Se o pedido não for atendido, você pode reclamar à [ANPD](https://www.gov.br/anpd).",
        },
      ],
    },
    {
      id: "incidentes",
      title: "Incidentes de segurança",
      summary:
        "Se um incidente puder trazer risco ou dano relevante, avisamos a loja em até 48 horas após a confirmação, para que ela cumpra os prazos dela com a ANPD.",
      blocks: [
        {
          type: "paragraph",
          text: "Incidente de segurança é qualquer evento que comprometa a confidencialidade, a integridade ou a disponibilidade de dados pessoais, como um acesso indevido ou um vazamento.",
        },
        {
          type: "list",
          items: [
            "**Dados dos clientes das lojas:** avisamos a loja afetada sem demora e em até 48 horas após confirmar o incidente, com o que soubermos até ali: o que aconteceu, que dados foram afetados e o que estamos fazendo. A comunicação à ANPD e aos titulares cabe à loja, como controladora, em até 3 dias úteis (Resolução CD/ANPD nº 15/2024).",
            "**Dados em que a Fidexa é controladora:** comunicamos a ANPD e os titulares nos prazos da Resolução CD/ANPD nº 15/2024.",
            "Todo incidente fica registrado, com as medidas tomadas, pelo prazo mínimo exigido pela ANPD.",
          ],
        },
      ],
    },
    {
      id: "encarregado",
      title: "Encarregado de dados",
      summary: `O encarregado de dados da Fidexa é ${DPO.name}. Ele é o canal entre a Fidexa, os titulares e a ANPD.`,
      blocks: [
        {
          type: "paragraph",
          text: `Nos termos do art. 41 da LGPD e da Resolução CD/ANPD nº 18/2024, a Fidexa indica como encarregado pelo tratamento de dados pessoais **${DPO.name}**, que atende pelo e-mail [${DPO.email}](mailto:${DPO.email}). Ele recebe pedidos e reclamações de titulares, comunicações da ANPD e orienta a equipe sobre proteção de dados.`,
        },
      ],
    },
  ],

  changelog: [
    {
      version: "1.0",
      date: "2026-10-12",
      changes: ["Primeira versão publicada."],
    },
  ],
};
